/**
 * Renders one date group of Business Office comments as {@link SingleComment} rows.
 *
 * @module allComments
 */
import { AllCommentsProps } from "../../../../../data-model/busniessOfficeNote";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { isValidString } from "../../../../../utils/stringModifications";
import SingleComment from "./singleComment/index";

/**
 * Prefers the comment's own `writeStatus` when it has non-whitespace content;
 * otherwise uses the order-level EV write-back status.
 *
 * @param commentWriteStatus - Status from the comment row (`write_status`)
 * @param evWriteBackStatus - Fallback from `generalOrderDetails.status`
 * @returns Status string to pass to {@link SingleComment}
 */
const resolveWriteStatus = (
  commentWriteStatus: string | undefined,
  evWriteBackStatus: string | undefined,
): string =>
  isValidString(commentWriteStatus)
    ? commentWriteStatus
    : (evWriteBackStatus ?? "");

/**
 * Maps each comment in a date group onto a {@link SingleComment}.
 *
 * @param props - Date label, comment rows, and order context
 * @returns The dated comment list
 */
const AllComments = (props: AllCommentsProps) => {
  return (
    <div className="all-comments-container mt-2 border-b border-dashed border-primaryGray-14 pb-4">
      <div className="all-comments-container--date mb-2 text-small font-bold">
        {props.commentDate}
      </div>
      <div className="all-comments-container--comments flex flex-col gap-4">
        {props.comments.map((comment, index) => (
          <SingleComment
            generalOrderDetails={
              props.generalOrderDetails ?? ({} as GeneralOrderDetails)
            }
            authOnFile={props.authOnFile ?? ({} as AuthOnFile)}
            writeBackComment={comment.writeBackComment || ""}
            key={`${comment.id}\u0001${props.commentDate}\u0001${index}`}
            showWriteBackNote={comment.showWriteBackNote}
            {...comment}
            onBoStatusChange={props.onBoStatusChange || (() => {})}
            onWriteBackCommentChange={
              props.onWriteBackCommentChange || (() => {})
            }
            boStatus={comment.boStatus || ""}
            index={index}
            writeStatus={resolveWriteStatus(
              comment.writeStatus,
              props.generalOrderDetails?.status?.ev_write_back_status,
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default AllComments;
